import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { toast } from 'sonner';
import type {
  Attribute,
  AttributesResponse,
  UpdateAttributePayload,
  AttributeGroup,
} from './types';
import { API_ENDPOINTS } from '@/config/config';
import { useEffectiveAttributes } from '@/hooks/useEffectiveAttributes';
import { invalidateAttributeValueCache } from '@/utils/cacheInvalidation';

const ATTRIBUTES_QUERY_KEY = (productId: string, locale?: string, channel?: string) => [
  'productAttributes',
  productId,
  locale ?? 'all',
  channel ?? 'all',
];

export const GROUPS_QUERY_KEY = (productId: string) => ['attributeGroups', productId];

export function useAttributes(productId: string, locale?: string, channel?: string) {
  // Use the effective attributes hook directly and transform to legacy format
  const effectiveQuery = useEffectiveAttributes(
    parseInt(productId),
    locale,
    channel,
    Boolean(productId)
  );

  return useQuery<AttributesResponse, Error>({
    queryKey: ATTRIBUTES_QUERY_KEY(productId, locale, channel),
    queryFn: async () => {
      if (!effectiveQuery.data) {
        throw new Error('No effective attributes data available');
      }

      // Transform effective attributes to legacy format
      const legacyAttributes: Attribute[] = [];

      effectiveQuery.data.forEach(group => {
        group.attributes.forEach(attr => {
          legacyAttributes.push({
            id: String(attr.value_id || attr.id),
            name: attr.label,
            value: attr.value,
            group: group.groupName,
            locale: attr.locale || '',
            channel: attr.channel || '',
            type: attr.data_type,
            product: parseInt(productId),
            // Additional fields for compatibility
            attribute_label: attr.label,
            attribute_code: attr.code,
            attribute_type: attr.data_type,
          });
        });
      });

      return { attributes: legacyAttributes };
    },
    enabled: Boolean(productId) && Boolean(effectiveQuery.data),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateAttribute(productId: string, locale?: string, channel?: string) {
  const queryClient = useQueryClient();
  const effectiveQuery = useEffectiveAttributes(parseInt(productId), locale, channel, false); // Don't auto-fetch

  return useMutation<
    Attribute,
    Error,
    { id: string; payload: UpdateAttributePayload },
    { previous?: AttributesResponse }
  >({
    mutationFn: async ({ id, payload }) => {
      const params: Record<string, string> = {};

      // Only add locale/channel to params if they are provided and not 'default'
      if (locale && locale !== 'default') {
        params.locale = locale;
      }
      if (channel && channel !== 'default') {
        params.channel = channel;
      }

      // Send the value as-is from formatValueForAPI
      // The Django tests show that JSONField accepts values directly
      const { data } = await axiosInstance.patch<Attribute>(
        `/api/products/${productId}/attributes/${id}/`,
        payload,
        { params }
      );
      return data;
    },
    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries({
        queryKey: ATTRIBUTES_QUERY_KEY(productId, locale, channel),
      });

      const previous = queryClient.getQueryData<AttributesResponse>(
        ATTRIBUTES_QUERY_KEY(productId, locale, channel)
      );

      if (previous) {
        const updated: AttributesResponse = {
          attributes: previous.attributes.map(attr =>
            attr.id === id ? { ...attr, value: payload.value } : attr
          ),
        };
        queryClient.setQueryData(ATTRIBUTES_QUERY_KEY(productId, locale, channel), updated);
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      toast.error('Failed to save attribute');
      if (context?.previous) {
        queryClient.setQueryData(
          ATTRIBUTES_QUERY_KEY(productId, locale, channel),
          context.previous
        );
      }
    },
    onSuccess: () => {
      toast.success('Attribute updated');
    },
    onSettled: (data, error, variables) => {
      // Try to find the attribute definition for smart cache invalidation
      const attributeId = parseInt(variables.id);
      let attribute = null;

      if (effectiveQuery.data) {
        // Find the attribute in the effective attributes data
        for (const group of effectiveQuery.data) {
          const foundAttr = group.attributes.find(attr => attr.id === attributeId);
          if (foundAttr) {
            attribute = foundAttr;
            break;
          }
        }
      }

      if (attribute) {
        // Use smart cache invalidation based on attribute properties
        invalidateAttributeValueCache(
          queryClient,
          attribute,
          parseInt(productId),
          locale || 'default',
          channel || 'default',
          false // enableGroups = false for ProductAttributesPanel
        );
      } else {
        // Fallback to current locale/channel invalidation if attribute not found
        queryClient.invalidateQueries({
          queryKey: ATTRIBUTES_QUERY_KEY(productId, locale, channel),
        });
        queryClient.invalidateQueries({
          queryKey: ['effectiveAttributesLegacy', productId, locale, channel],
        });
        queryClient.invalidateQueries({
          queryKey: ['product', parseInt(productId), 'effectiveAttributes', locale, channel],
        });

        // Invalidate completeness-related queries to update the completeness card
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'summary'] });
        queryClient.invalidateQueries({
          queryKey: ['dashboard', 'incomplete-products'],
        });
      }
    },
  });
}

export function useDeleteAttribute(productId: string, locale?: string, channel?: string) {
  const queryClient = useQueryClient();
  const effectiveQuery = useEffectiveAttributes(parseInt(productId), locale, channel, false); // Don't auto-fetch

  return useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const params: Record<string, string> = {};

      // Only add locale/channel to params if they are provided and not 'default'
      if (locale && locale !== 'default') {
        params.locale = locale;
      }
      if (channel && channel !== 'default') {
        params.channel = channel;
      }

      await axiosInstance.delete(`/api/products/${productId}/attributes/${id}/`, { params });
    },
    onSuccess: (data, variables) => {
      // Try to find the attribute definition for smart cache invalidation
      const attributeId = parseInt(variables.id);
      let attribute = null;

      if (effectiveQuery.data) {
        // Find the attribute in the effective attributes data
        for (const group of effectiveQuery.data) {
          const foundAttr = group.attributes.find(attr => attr.id === attributeId);
          if (foundAttr) {
            attribute = foundAttr;
            break;
          }
        }
      }

      if (attribute) {
        // Use smart cache invalidation based on attribute properties
        invalidateAttributeValueCache(
          queryClient,
          attribute,
          parseInt(productId),
          locale || 'default',
          channel || 'default',
          false // enableGroups = false for ProductAttributesPanel
        );
      } else {
        // Fallback to current locale/channel invalidation if attribute not found
        queryClient.invalidateQueries({
          queryKey: ATTRIBUTES_QUERY_KEY(productId, locale, channel),
        });
        queryClient.invalidateQueries({
          queryKey: ['effectiveAttributesLegacy', productId, locale, channel],
        });
        queryClient.invalidateQueries({
          queryKey: ['product', parseInt(productId), 'effectiveAttributes', locale, channel],
        });

        // Invalidate completeness-related queries to update the completeness card
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'summary'] });
        queryClient.invalidateQueries({
          queryKey: ['dashboard', 'incomplete-products'],
        });
      }
    },
    onError: () => toast.error('Delete failed'),
  });
}

export function useAttributeGroups(
  productId: string,
  locale?: string,
  channel?: string,
  enabled?: boolean
) {
  // Use the effective attributes hook directly and transform to legacy format
  const effectiveQuery = useEffectiveAttributes(
    parseInt(productId),
    locale,
    channel,
    enabled !== false && Boolean(productId)
  );

  return useQuery<AttributeGroup[], Error>({
    queryKey: ['effectiveAttributeGroupsLegacy', productId, locale, channel],
    queryFn: async () => {
      if (!effectiveQuery.data) {
        throw new Error('No effective attributes data available');
      }

      // Transform effective attribute groups to legacy format
      const legacyGroups: AttributeGroup[] = effectiveQuery.data.map(group => ({
        id: group.groupId,
        name: group.groupName,
        description: `Group with ${group.attributes.length} attributes`,
        items: group.attributes.map(attr => ({
          id: attr.value_id || attr.id,
          attribute: attr.id,
          value: attr.value,
          value_id: attr.value_id || undefined,
          locale: attr.locale,
          channel: attr.channel,
        })),
      }));

      return legacyGroups;
    },
    enabled: enabled !== false && Boolean(productId) && Boolean(effectiveQuery.data),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAttribute(productId: string, locale?: string, channel?: string) {
  const queryClient = useQueryClient();
  const effectiveQuery = useEffectiveAttributes(parseInt(productId), locale, channel, false); // Don't auto-fetch

  return useMutation<
    Attribute,
    Error,
    { attributeId: number; value: string; locale?: string; channel?: string }
  >({
    mutationFn: async ({
      attributeId,
      value,
      locale: attributeLocale,
      channel: attributeChannel,
    }) => {
      const params: Record<string, string> = {};

      // Only use locale/channel if explicitly provided in the mutateAsync call (not undefined)
      // If the caller passes undefined, don't fall back to the hook's default locale/channel
      const finalLocale = attributeLocale !== undefined ? attributeLocale : undefined;
      const finalChannel = attributeChannel !== undefined ? attributeChannel : undefined;

      if (finalLocale && finalLocale !== 'default') {
        params.locale = finalLocale;
      }
      if (finalChannel && finalChannel !== 'default') {
        params.channel = finalChannel;
      }

      // Send the value as-is from formatValueForAPI
      // The Django tests show that JSONField accepts values directly
      const payload: any = {
        attribute: attributeId,
        product: Number(productId),
        value: value,
      };

      // Only add locale/channel to payload if they are provided and not 'default'
      if (finalLocale && finalLocale !== 'default') {
        payload.locale = finalLocale;
      }
      if (finalChannel && finalChannel !== 'default') {
        payload.channel = finalChannel;
      }

      const { data } = await axiosInstance.post<Attribute>(
        `/api/products/${productId}/attributes/`,
        payload,
        { params }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success('Attribute added');

      // Try to find the attribute definition for smart cache invalidation
      let attribute = null;

      if (effectiveQuery.data) {
        // Find the attribute in the effective attributes data
        for (const group of effectiveQuery.data) {
          const foundAttr = group.attributes.find(attr => attr.id === variables.attributeId);
          if (foundAttr) {
            attribute = foundAttr;
            break;
          }
        }
      }

      if (attribute) {
        // Use smart cache invalidation based on attribute properties
        invalidateAttributeValueCache(
          queryClient,
          attribute,
          parseInt(productId),
          locale || 'default',
          channel || 'default',
          false // enableGroups = false for ProductAttributesPanel
        );
      } else {
        // Fallback to current locale/channel invalidation if attribute not found
        queryClient.invalidateQueries({
          queryKey: ATTRIBUTES_QUERY_KEY(productId, locale, channel),
        });
        queryClient.invalidateQueries({
          queryKey: ['effectiveAttributesLegacy', productId, locale, channel],
        });
        queryClient.invalidateQueries({
          queryKey: ['product', parseInt(productId), 'effectiveAttributes', locale, channel],
        });

        // Invalidate completeness-related queries to update the completeness card
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'summary'] });
        queryClient.invalidateQueries({
          queryKey: ['dashboard', 'incomplete-products'],
        });
      }
    },
    onError: () => toast.error('Failed to add attribute'),
  });
}

export function useAllAttributes(enabled: boolean = false) {
  return useQuery<Attribute[], Error>({
    queryKey: ['allAttributes'],
    queryFn: async () => {
      const { data } = await axiosInstance.get(API_ENDPOINTS.imports.attributes || '');
      return data;
    },
    enabled: enabled, // Only load when explicitly enabled
    staleTime: 10 * 60 * 1000,
  });
}

export function useAllAttributeGroups() {
  return useQuery<AttributeGroup[], Error>({
    queryKey: ['allAttributeGroups'],
    queryFn: async () => {
      const { data } = await axiosInstance.get(API_ENDPOINTS.attributeGroups.list || '');
      return Array.isArray(data) ? data : [];
    },
    staleTime: 10 * 60 * 1000,
    // No enabled condition - always load regardless of productId
  });
}
